/**
 * SOFSEK — App User Groups (§2 group risk analysis). Nested teams whose combined
 * permissions get evaluated as a single collective profile (Phase 4 wires the
 * actual group-level scan into the violation lifecycle). This screen also
 * surfaces sfk_find_group_cycles() — an advisory check for a group whose own
 * ancestor chain loops back on itself (§29).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { useActiveLocale } from '../../lib/use_active_locale';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.APP_USER_GROUP.LIST';
const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface GroupRow {
  group_id: number;
  parent_group_id: number | null;
  internal_code: string;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}
interface MemberRow { member_id: number; group_id: number; app_user_id: number; }

function tlName(list: { locale_code: string; display_text: string }[], loc: string): string {
  return list.find(x => x.locale_code === loc)?.display_text ?? list.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'display_text', header: 'Group', defaultWidth: 220 },
  { key: 'internal_code', header: 'Code', defaultWidth: 160 },
  { key: 'parent', header: 'Parent', defaultWidth: 200 },
  { key: 'member_count', header: 'Members', defaultWidth: 100 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function GroupInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  // §17.B14 — names each language in the reader's own language.
  const locale = useActiveLocale();
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [subjects, setSubjects] = useState<CodeOpt[]>([]);
  const [cycles, setCycles] = useState<{ group_id: number; cycle_path: number[] }[]>([]);
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<GroupRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [groupRes, memberRes, subjRes, cycleRes] = await Promise.all([
        apiDb.from('sfk_app_user_group').select('group_id, parent_group_id, internal_code, is_active, sfk_app_user_group_tl ( locale_code, display_text )').order('internal_code'),
        apiDb.from('sfk_app_user_group_member').select('member_id, group_id, app_user_id'),
        apiDb.from('sfk_subject_profile').select('user_id, csm_user_master!fk_sfk_subject_profile_user ( display_name )'),
        apiDb.rpc('sfk_find_group_cycles'),
      ]);
      if (groupRes.error) throw groupRes.error;
      if (memberRes.error) throw memberRes.error;
      if (subjRes.error) throw subjRes.error;
      if (cycleRes.error) throw cycleRes.error;
      setGroups(((groupRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        group_id: r.group_id as number,
        parent_group_id: r.parent_group_id as number | null,
        internal_code: r.internal_code as string,
        is_active: r.is_active as 'Y' | 'N',
        tl: (r.sfk_app_user_group_tl ?? []) as GroupRow['tl'],
      })));
      setMembers((memberRes.data ?? []) as unknown as MemberRow[]);
      setSubjects(((subjRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.user_id), name: ((r.csm_user_master ?? {}) as { display_name?: string }).display_name ?? String(r.user_id) })));
      setCycles((cycleRes.data ?? []) as unknown as { group_id: number; cycle_path: number[] }[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const groupName = (id: number | null) => (id == null ? t('sfk.app_user_group.root', '(root)') : tlName(groups.find(g => g.group_id === id)?.tl ?? [], 'EN_US'));
  const memberCount = (groupId: number) => members.filter(m => m.group_id === groupId).length;
  const subjectName = (id: number) => subjects.find(s => s.code === String(id))?.name ?? String(id);

  // §17.B13 — related children: this group's members + its sub-groups.
  const childGroupsOf = (groupId: number) => groups.filter(g => g.parent_group_id === groupId);
  const hasRelated = (groupId: number) => memberCount(groupId) > 0 || childGroupsOf(groupId).length > 0
    || (groups.find(r => r.group_id === groupId)?.tl.length ?? 0) > 0;
  const buildRelatedGroups = (groupId: number): RelatedChildGroup[] => [
    {
      key: 'members', title: t('sfk.app_user_group.members', 'Members'),
      items: members.filter(m => m.group_id === groupId).map(m => ({ id: m.member_id, label: subjectName(m.app_user_id) })),
    },
    {
      key: 'subgroups', title: t('sfk.app_user_group.subgroups', 'Sub-groups'),
      items: childGroupsOf(groupId).map(g => ({ id: g.group_id, label: tlName(g.tl, 'EN_US'), title: g.internal_code })),
    },

    buildTranslationsGroup(groups.find(r => r.group_id === groupId)?.tl, t, locale),
  ];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return groups.filter(g =>
      (!q || g.internal_code.toLowerCase().includes(q) || tlName(g.tl, 'EN_US').toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(g.is_active)));
  }, [groups, filter, activeFacet]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.app_user_group.title', 'App User Groups')}
          description={t('sfk.app_user_group.description', 'Nested teams whose combined permissions are evaluated as one collective profile.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: groups.length, label: t('sfk.app_user_group.stat.total', 'Groups') },
          { value: groups.filter(g => g.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: members.length, label: t('sfk.app_user_group.stat.members', 'Memberships') },
        ]} />
        {cycles.length > 0 && (
          <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">
            {t('sfk.app_user_group.cycle_warning', 'Circular group nesting detected')}: {cycles.map(c => groupName(c.group_id)).join(', ')}
          </div>
        )}
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('common.boolean.active', 'Active')}
              options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]}
              selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || activeFacet.length > 0} onClear={() => { setFilter(''); setActiveFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(g => (
                  <Fragment key={g.group_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(g.group_id)} hasChildren={hasRelated(g.group_id)} onToggle={() => detail.toggle(g.group_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'display_text' && tlName(g.tl, 'EN_US')}
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{g.internal_code}</span>} {/* fk-display-ok: group's own code */}
                        {col.key === 'parent' && groupName(g.parent_group_id)}
                        {col.key === 'member_count' && memberCount(g.group_id)}
                        {col.key === 'is_active' && <YesNoBadge value={g.is_active} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(g)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(g.group_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(g.group_id)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <GroupModal session={session} row={editing} groups={groups} subjects={subjects} members={members}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function GroupModal({ session, row, groups, subjects, members, onClose, onSaved }: {
  session: AuthSession; row: GroupRow | null; groups: GroupRow[]; subjects: CodeOpt[]; members: MemberRow[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [parentId, setParentId] = useState(row?.parent_group_id != null ? String(row.parent_group_id) : '');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' })));
  const [selectedMembers, setSelectedMembers] = useState<number[]>(row ? members.filter(m => m.group_id === row.group_id).map(m => m.app_user_id) : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleMember = (subjectId: number) =>
    setSelectedMembers(cur => cur.includes(subjectId) ? cur.filter(x => x !== subjectId) : [...cur, subjectId]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      const payload = { parent_group_id: parentId ? Number(parentId) : null, is_active: isActive ? 'Y' : 'N' };
      let groupId = row?.group_id;
      if (row === null) {
        const { row_id, error: e } = await routedInsert(session, 'sfk_app_user_group', { internal_code: code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
        groupId = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_app_user_group', 'group_id', row.group_id, payload);
        if (e) throw new Error(e.message);
      }
      if (groupId != null) {
        await Promise.all(
          translations.filter(tr => tr.display_text?.trim()).map(tr =>
            routedInsert(session, 'sfk_app_user_group_tl', { group_id: groupId, locale_code: tr.locale_code, display_text: tr.display_text }))
        );
        const existing = members.filter(m => m.group_id === groupId);
        const toRemove = existing.filter(m => !selectedMembers.includes(m.app_user_id));
        const toAdd = selectedMembers.filter(sid => !existing.some(m => m.app_user_id === sid));
        await Promise.all([
          ...toRemove.map(m => routedDelete(session, 'sfk_app_user_group_member', 'member_id', m.member_id)),
          ...toAdd.map(sid => routedInsert(session, 'sfk_app_user_group_member', { group_id: groupId, app_user_id: sid })),
        ]);
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.app_user_group.new', 'New Group') : `${t('common.edit', 'Edit')} — ${row?.internal_code ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="parent_group_id" label={t('sfk.app_user_group.parent', 'Parent Group')}>
        <label className={labelCls}>{t('sfk.app_user_group.parent', 'Parent Group')}</label>
        <select value={parentId} onChange={e => setParentId(e.target.value)} className={`${inputCls} bg-white`}>
          <option value="">{t('sfk.app_user_group.root', '(root)')}</option>
          {groups.filter(g => g.is_active === 'Y' && g.group_id !== row?.group_id).map(g => <option key={g.group_id} value={g.group_id}>{tlName(g.tl, 'EN_US')}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      <div className="mb-3">
        <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('sfk.app_user_group.members', 'Members')}</span>
        <div className="max-h-40 overflow-auto rounded border border-csm-border">
          {subjects.map(s => (
            <label key={s.code} className="flex items-center gap-2 border-b border-csm-border-soft px-2 py-1 text-csm-sm last:border-b-0">
              <input type="checkbox" checked={selectedMembers.includes(Number(s.code))} onChange={() => toggleMember(Number(s.code))} className="h-4 w-4 accent-gray-700" />
              {s.name}
            </label>
          ))}
          {subjects.length === 0 && <div className="px-2 py-2 text-csm-xs italic text-csm-watermark">{t('common.no_records', 'No records')}</div>}
        </div>
      </div>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkAppUserGroupPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <GroupInner session={session} />
    </PersonalizationProvider>
  );
}
